import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  Stack,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import type { InsuranceParamsDto, Item, VehiclesIdentificationDto } from 'BrokerTsLib/src/dtos'
import { Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect, useRef, useState } from 'react'

import { Apis } from '@/appSettings/AppSettings'
import { FormLabel } from '@/components/FormLabel'
import { FormRow } from '@/containers/forms/FormRow'
import { RadioGroup as CustomRadioGroup } from '@/containers/forms/RadioGroup'
import { useAuth } from '@/context/AuthContext'
import { usePartner } from '@/context/PartnerContext'
import { useSelections, useUrl } from '@/hooks'
import type { VehicleIdentificationLinkPropsDto } from '@/hooks/useDocuments'
import { useVehicleIdentification } from '@/hooks/useVehicle'
import { Cloud } from '@/images/Cloud'

import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import { NumberFormControl } from 'BrokerTsLib/src/components'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import { isIn } from 'BrokerTsLib/src/Utils'
import { VehiclePriceTable } from './components/VehiclePriceTable'

const priceTypeSort = (x: Item, y: Item, partner?: string): -1 | 0 | 1 => {
  if (['Essox', 'AutoEsa', 'DirectAuto'].includes(partner ?? '') && x.id == Selection.VehicleValuation)
    return -1

  if (y.id == Selection.VehicleValuation)
    return -1

  return (x.displayOrder ?? 0) < (y.displayOrder ?? 0) ? -1 : 0
}

export const VehiclePriceSelection = ({
  isDisabled = false,
  extraWizard = false,
  insuranceParamsId
}: {
  isDisabled?: boolean
  extraWizard?: boolean
  insuranceParamsId?: string
}) => {
  const { app } = useApp()
  const isPoleas = app == Apps.publicPoleas
  const { user } = useAuth()
  const { currentPartner } = usePartner()
  const cebiCat = !!user?.cebiCat || isPoleas

  const { data: dataCenaVozidlaZ } = useSelections(SelectionType.CenaVozidlaZ)

  const {
    control,
    formState: { errors },
    setValue,
    getValues,
    watch,
    trigger,
    setError
  } = useFormContext<InsuranceParamsDto>()

  const priceType = watch('vehicle.priceType')

  // identifikace vozidla
  const { mutateAsync: getVehicleIdentification, isLoading: isLoadingVehicleIdentification } = useVehicleIdentification()
  const [vehicleIdentificationData, setVehicleIdentificationData] = useState<VehiclesIdentificationDto | null>(null)
  const [showVehicleEvaluation, setShowVehicleEvaluation] = useState(false)

  const url = useUrl(Apis.document)
  const getVehicleIdentificationLink = (data: VehicleIdentificationLinkPropsDto) => {
    let idParam = ''
    if (data.paramsId) idParam = `&id=${data.paramsId}`
    let vinParam = ''
    if (data.vin) vinParam = `&vin=${data.vin}`
    let spzParam = ''
    if (data.spz) spzParam = `&registrationMark=${data.spz}`

    return `${url}/GetIdentificatedVehiclePdf?vehicleId=${data.vehicleId}${vinParam}${spzParam}${idParam}`
  }

  const getVehicleIdentificationParams = () => {
    return {
      paramsId: insuranceParamsId,
      spz: getValues('vehicle.licensePlate'),
      vin: getValues('vehicle.vin'),
      techCertNumber: getValues('vehicle.cisloTechnickehoPrukazu')
    }
  }

  const setVehicleIndentificationLinks = (identificationData: VehiclesIdentificationDto) => {
    if (identificationData?.vehicles && identificationData?.vehicles?.length > 0) {
      const identParams = getVehicleIdentificationParams()
      for (let iv = 0; iv < identificationData.vehicles.length; iv++) {
        const vehicleElement = identificationData.vehicles[iv]
        if (vehicleElement.id)
          vehicleElement.pdfLink = getVehicleIdentificationLink({
            vehicleId: vehicleElement.id,
            spz: identParams.spz,
            vin: identParams.vin,
            paramsId: identParams.paramsId
          })
      }
    }
  }

  useEffect(() => {
    if (extraWizard && priceType !== Selection.Invoice) {
      setValue('vehicle.priceType', Selection.Invoice)
    }
  }, [extraWizard, setValue, priceType])

  const [valuationClicked, setValuationClicked] = useState(false)

  return (
    <>
      {!extraWizard &&
        <Flex gap={8} justifyContent='space-between' width='100%' flexWrap='wrap'>
          <FormControl isInvalid={!!errors.vehicle?.priceType}>
            <FormLabel>Cena vozidla z</FormLabel>

            <FormHelperText>
              Vyberte zdroj, ze kterého čerpáte cenu vozidla. V případě, že cenu
              neznáte, je možné vozidlo ocenit.
            </FormHelperText>

            <CustomRadioGroup
              name="vehicle.priceType"
              onChangeValueTransform={(val) => parseInt(val)}
              onChange={(val) => {
                setValue('vehicle.priceType', parseInt(val))
              }}
              isDisabled={isDisabled}
            >
              {/* custom razeni, aby tlacitko na oceneni bylo hned za volbou "Ocenění vozidla" */}
              {dataCenaVozidlaZ?.data
                .filter(x => cebiCat || x.id != Selection.VehicleValuation)
                .filter(x => !isPoleas || x.id != Selection.Invoice)
                .sort((x, y) => priceTypeSort(x, y, currentPartner?.partner))
                .map((item) => (
                  <CustomTooltip key={item.id}
                    boxProps={{ as: HStack, gap: 3 }}
                    label={(item.id == Selection.VehicleValuation) ? 'Pro ocenění vozidla klikněte na tlačítko "Ocenit vozidlo".' : undefined}
                    hasArrow>
                    <Radio value={item.id.toString()} isDisabled={(Selection.VehicleValuation == item.id && !user?.allowChangePriceTypeToValuation) || isDisabled}>
                      {item.description}
                    </Radio>
                  </CustomTooltip>
                ))}
            </CustomRadioGroup>

            {errors.vehicle?.priceType && <FormErrorMessage>{errors.vehicle.priceType.message}</FormErrorMessage>}
          </FormControl>

          {cebiCat && !isDisabled &&
            <Button
              type="button"
              variant="solid"
              alignSelf="end"
              whiteSpace='nowrap'
              isLoading={isLoadingVehicleIdentification}
              loadingText='Načítám...'
              onClick={() => {
                // zvaliduji se povinne inputy pro oceneni
                setValue('vehicle.useVehicleEvaluation', true)
                setValuationClicked(true)
                trigger([
                  'vehicle.firstRegistrationDate',
                  'vehicle.mileage',
                  'vehicle.licensePlate',
                  'vehicle.vin'
                ]).then((valid) => {
                  if (!valid) return

                  setValue('vehicle.useVehicleEvaluation', false)
                  setShowVehicleEvaluation(false)

                  getVehicleIdentification(getVehicleIdentificationParams())
                    .then(({ data }) => {
                      setVehicleIndentificationLinks(data)
                      setVehicleIdentificationData(data)
                      setShowVehicleEvaluation(true)
                    })
                    .catch(() => {
                      setError('vehicle.vin', { message: 'Nepodařilo se indentifikovat vozidlo' })
                    })
                })
              }}
            >
              <Cloud color="secondary.500" />
              Ocenit vozidlo
            </Button>
          }
        </Flex>
      }

      {(priceType == Selection.VehicleValuation || valuationClicked) && errors.vehicle &&
        (errors.vehicle.licensePlate || errors.vehicle.vin || errors.vehicle.mileage || errors.vehicle.firstRegistrationDate) &&
        <Stack direction='column'>
          {errors.vehicle.licensePlate && <Text variant='error' fontSize={'sm'}>{errors.vehicle.licensePlate.message}</Text>}
          {errors.vehicle.vin && <Text variant='error' fontSize={'sm'}>{errors.vehicle.vin.message}</Text>}
          {errors.vehicle.mileage && <Text variant='error' fontSize={'sm'}>{errors.vehicle.mileage.message}</Text>}
          {errors.vehicle.firstRegistrationDate && <Text variant='error' fontSize={'sm'}>{errors.vehicle.firstRegistrationDate.message}</Text>}
        </Stack>}

      {showVehicleEvaluation && !isDisabled &&
        <>
          {!vehicleIdentificationData?.vehicles?.length &&
            <Text variant='warning'>Vozidlo se nepodařilo identifikovat</Text>}

          {vehicleIdentificationData?.vehicles && vehicleIdentificationData?.vehicles?.length > 0 &&
            <VehiclePriceTable
              vehicleIdentificationData={vehicleIdentificationData}
              paramsId={insuranceParamsId}
              firstRegistrationDate={getValues('vehicle.firstRegistrationDate')!}
              mileage={getValues('vehicle.mileage')!}
              licensePlate={getValues('vehicle.licensePlate')}
              vin={getValues('vehicle.vin')}
              onUsePriceButtonClick={(usedPrice, withTax) => {
                setValue('vehicle.price', usedPrice)
                setValue('vehicle.priceIncludeVat', withTax)
                setValue('vehicle.priceType', Selection.VehicleValuation)
                setShowVehicleEvaluation(false)
              }} />}
        </>}

      <FormControl sx={{ w: '100%' }} isInvalid={!!errors.vehicle?.price} isRequired>
        {!extraWizard &&
          <>
            <FormLabel>Cena vozidla</FormLabel>

            <FormHelperText>
              Cena/pojistná částka vozidla je povinná pro pojištění HAV a pro
              kalkulaci všech balíčků <strong>Allianz</strong> mimo Komfort.
            </FormHelperText>
          </>
        }

        {extraWizard &&
          <>
            <FormLabel>Cena vozidla z faktury (Kč)</FormLabel>

            <FormHelperText>
              Pokud je u právnických osob a fyzických osob podnikajících na vozidlo uplatněn odečet DPH, udávejte cenu bez DPH.
              Ve všech ostatních případech zadejte cenu s DPH.
            </FormHelperText>
          </>
        }

        <FormRow sx={{ gap: 16, alignItems: 'center' }}>
          <NumberFormControl control={control} name='vehicle.price'
            onChange={() => {
              if (!extraWizard && !isIn(priceType, Selection.AccordingPolicyHolder, Selection.Invoice) && !user?.allowChangePriceTypeToValuation)
                setValue('vehicle.priceType', Selection.AccordingPolicyHolder)
            }}
            isDisabled={isDisabled}
          />

          <CustomRadioGroup
            name="vehicle.priceIncludeVat"
            onChangeValueTransform={(val) => val == '1'}
            valueTransform={(val: boolean) => val ? '1' : '0'}
            isDisabled={isDisabled}
            onChange={(value) => {
              const price = getValues('vehicle.price')
              const checked = value == '1'

              if (price) {
                const priceInt = price ? parseInt(price!.toString()) : 0
                const resultPrice = Math.round((checked ? priceInt * 1.21 : priceInt / 1.21))
                setValue('vehicle.price', resultPrice)
              }

              setValue('vehicle.priceIncludeVat', checked)
            }}
          >
            <Radio value='1'>
              s DPH
            </Radio>

            <Radio value='0'>
              bez DPH
            </Radio>

          </CustomRadioGroup>
        </FormRow>

        {errors.vehicle?.price && <FormErrorMessage>{errors.vehicle?.price.message}</FormErrorMessage>}
        {errors.vehicle?.priceIncludeVat && <FormErrorMessage>{errors.vehicle?.priceIncludeVat.message}</FormErrorMessage>}

      </FormControl>

      <PriceIncludeVatWarning isDisabled={isDisabled} />
    </>
  )
}

/** Upozornení (modal dialog), který se zobrazí, když cena vozidla je s DPH a pojistník je zároveň plátcem DPH.
 * Zobrazí se pouze jednou.
 */
const PriceIncludeVatWarning = ({ isDisabled }: { isDisabled: boolean }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { watch } = useFormContext()
  const priceIncludeVat = watch('vehicle.priceIncludeVat')
  const isVatPayer = watch('policyHolder.person.isVatPayer')
  const alreadyShown = useRef(false)

  useEffect(() => {
    if (priceIncludeVat && isVatPayer && !alreadyShown.current && !isDisabled) {
      onOpen()
      alreadyShown.current = true
    }
  }, [priceIncludeVat, isVatPayer, onOpen, isDisabled])

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Upozornění</ModalHeader>
        <ModalBody>
          <Text>Pojistník je plátcem DPH a cena vozidla je uvedena včetně DPH. Prosím, zkontrolujte, zda je cena vozidla správně uvedena.</Text>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>OK</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}